import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { guestService } from '@/services/guestService'
import type { CustomerAppointmentDto, OrderListDto, GuestDashboardSummary } from '@/types/guest'

const GUEST_SESSION_KEY = 'guest_session'

interface GuestSession {
  token: string
  email: string
  expiresAt: string
}

export const useGuestStore = defineStore('guest', () => {
  // State
  const guestToken = ref<string | null>(null)
  const guestEmail = ref<string | null>(null)
  const tokenExpiry = ref<Date | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Cached data
  const bookings = ref<CustomerAppointmentDto[]>([])
  const orders = ref<OrderListDto[]>([])
  const dashboardSummary = ref<GuestDashboardSummary | null>(null)

  // Code sending state
  const codeSentAt = ref<Date | null>(null)
  const cooldownSeconds = ref(0)
  let cooldownInterval: number | null = null

  // Router
  const router = useRouter()

  // Persistence helpers
  function saveSessionToStorage(): void {
    if (guestToken.value && guestEmail.value && tokenExpiry.value) {
      const session: GuestSession = {
        token: guestToken.value,
        email: guestEmail.value,
        expiresAt: tokenExpiry.value.toISOString()
      }
      localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session))
      console.log('Guest session saved to storage')
    }
  }

  function loadSessionFromStorage(): boolean {
    try {
      const stored = localStorage.getItem(GUEST_SESSION_KEY)
      if (!stored) return false

      const session: GuestSession = JSON.parse(stored)
      const expiresAt = new Date(session.expiresAt)

      // Check if session is still valid
      if (new Date() >= expiresAt) {
        console.log('Stored guest session has expired, clearing...')
        localStorage.removeItem(GUEST_SESSION_KEY)
        return false
      }

      // Restore session
      guestToken.value = session.token
      guestEmail.value = session.email
      tokenExpiry.value = expiresAt

      // Schedule auto-logout for remaining time
      scheduleAutoLogout()

      console.log(`Guest session restored from storage, expires at: ${expiresAt}`)
      return true
    } catch (err) {
      console.error('Error loading guest session from storage:', err)
      localStorage.removeItem(GUEST_SESSION_KEY)
      return false
    }
  }

  function clearSessionFromStorage(): void {
    localStorage.removeItem(GUEST_SESSION_KEY)
    console.log('Guest session cleared from storage')
  }

  // Getters
  const isGuestAuthenticated = computed(() => {
    if (!guestToken.value || !tokenExpiry.value) return false
    return new Date() < tokenExpiry.value
  })

  const remainingSessionTime = computed(() => {
    if (!tokenExpiry.value) return 0
    const remaining = Math.max(0, tokenExpiry.value.getTime() - Date.now())
    return Math.floor(remaining / 1000) // seconds
  })

  const formattedRemainingTime = computed(() => {
    const seconds = remainingSessionTime.value
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  })

  const upcomingBookings = computed(() => {
    const now = new Date()
    return bookings.value
      .filter(b => new Date(b.scheduledAtUTC) >= now)
      .sort((a, b) => new Date(a.scheduledAtUTC).getTime() - new Date(b.scheduledAtUTC).getTime())
  })

  const pastBookings = computed(() => {
    const now = new Date()
    return bookings.value
      .filter(b => new Date(b.scheduledAtUTC) < now)
      .sort((a, b) => new Date(b.scheduledAtUTC).getTime() - new Date(a.scheduledAtUTC).getTime())
  })

  const activeOrders = computed(() => {
    const activeStatuses = ['Pending', 'PendingPayment', 'Paid', 'Confirmed', 'Processing', 'Ready', 'ReadyForCollection', 'OutForDelivery']
    return orders.value.filter(o => activeStatuses.includes(o.statusDisplay))
  })

  const completedOrders = computed(() => {
    const activeStatuses = ['Pending', 'PendingPayment', 'Paid', 'Confirmed', 'Processing', 'Ready', 'ReadyForCollection', 'OutForDelivery']
    return orders.value.filter(o => !activeStatuses.includes(o.statusDisplay))
  })

  // Actions
  async function sendVerificationCode(email: string): Promise<{ success: boolean; message?: string }> {
    isLoading.value = true
    error.value = null

    try {
      const response = await guestService.sendCode(email)

      // Start cooldown timer
      codeSentAt.value = new Date()
      startCooldownTimer()

      return { success: true, message: response.message }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send verification code. Please try again.'
      error.value = errorMessage

      // Check for rate limit
      if (err.response?.status === 429) {
        cooldownSeconds.value = err.response?.data?.retryAfterSeconds || 60
        startCooldownTimer()
      }

      return { success: false, message: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  async function verifyCode(email: string, code: string): Promise<{ success: boolean; message?: string }> {
    isLoading.value = true
    error.value = null

    try {
      const response = await guestService.verifyCode(email, code)

      // Store token data
      guestToken.value = response.token
      guestEmail.value = email.toLowerCase().trim()
      tokenExpiry.value = new Date(response.expiresAt)

      // Save to localStorage for persistence across page refreshes
      saveSessionToStorage()

      // Schedule auto-logout
      scheduleAutoLogout()

      console.log(`Guest verified: ${guestEmail.value}, expires at: ${tokenExpiry.value}`)

      return { success: true }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Invalid or expired verification code. Please request a new code.'
      error.value = errorMessage
      return { success: false, message: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchBookings(): Promise<void> {
    if (!isGuestAuthenticated.value) return

    isLoading.value = true
    error.value = null

    try {
      const response = await guestService.getBookings()
      bookings.value = [...response.upcoming, ...response.past]
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch bookings.'
      console.error('Error fetching guest bookings:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchOrders(): Promise<void> {
    if (!isGuestAuthenticated.value) return

    isLoading.value = true
    error.value = null

    try {
      const response = await guestService.getOrders()
      orders.value = [...response.active, ...response.completed]
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch orders.'
      console.error('Error fetching guest orders:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchDashboard(): Promise<void> {
    if (!isGuestAuthenticated.value) return

    isLoading.value = true
    error.value = null

    try {
      dashboardSummary.value = await guestService.getDashboard()
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch dashboard.'
      console.error('Error fetching guest dashboard:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchAllData(): Promise<void> {
    if (!isGuestAuthenticated.value) return

    isLoading.value = true
    error.value = null

    try {
      // Fetch bookings and orders in parallel
      await Promise.all([fetchBookings(), fetchOrders()])
    } finally {
      isLoading.value = false
    }
  }

  function clearGuestSession(): void {
    guestToken.value = null
    guestEmail.value = null
    tokenExpiry.value = null
    bookings.value = []
    orders.value = []
    dashboardSummary.value = null
    error.value = null

    // Clear from localStorage
    clearSessionFromStorage()

    // Stop cooldown timer
    if (cooldownInterval) {
      clearInterval(cooldownInterval)
      cooldownInterval = null
    }
    cooldownSeconds.value = 0
    codeSentAt.value = null

    console.log('Guest session cleared')
  }

  function logout(): void {
    clearGuestSession()
    router.push('/guest/verify')
  }

  // Helper functions
  let autoLogoutTimeout: number | null = null

  function scheduleAutoLogout(): void {
    // Clear existing timeout
    if (autoLogoutTimeout) {
      clearTimeout(autoLogoutTimeout)
      autoLogoutTimeout = null
    }

    if (!tokenExpiry.value) return

    const timeUntilExpiry = tokenExpiry.value.getTime() - Date.now()

    if (timeUntilExpiry > 0) {
      console.log(`Guest session auto-logout scheduled in ${Math.floor(timeUntilExpiry / 1000)} seconds`)

      autoLogoutTimeout = window.setTimeout(() => {
        console.log('Guest session expired, logging out...')
        clearGuestSession()
        router.push({
          name: 'GuestVerify',
          query: { expired: 'true' }
        })
      }, timeUntilExpiry)
    }
  }

  function startCooldownTimer(): void {
    // Clear existing interval
    if (cooldownInterval) {
      clearInterval(cooldownInterval)
    }

    // Set initial cooldown if not already set
    if (cooldownSeconds.value <= 0) {
      cooldownSeconds.value = 60
    }

    cooldownInterval = window.setInterval(() => {
      cooldownSeconds.value = Math.max(0, cooldownSeconds.value - 1)

      if (cooldownSeconds.value <= 0 && cooldownInterval) {
        clearInterval(cooldownInterval)
        cooldownInterval = null
      }
    }, 1000)
  }

  function resetStore(): void {
    clearGuestSession()
  }

  // Initialize: try to restore session from localStorage on store creation
  loadSessionFromStorage()

  return {
    // State
    guestToken,
    guestEmail,
    tokenExpiry,
    isLoading,
    error,
    bookings,
    orders,
    dashboardSummary,
    cooldownSeconds,
    codeSentAt,

    // Getters
    isGuestAuthenticated,
    remainingSessionTime,
    formattedRemainingTime,
    upcomingBookings,
    pastBookings,
    activeOrders,
    completedOrders,

    // Actions
    sendVerificationCode,
    verifyCode,
    fetchBookings,
    fetchOrders,
    fetchDashboard,
    fetchAllData,
    clearGuestSession,
    logout,
    resetStore
  }
})
